// Chalo's HUD and crowd cues (docs/plans/2026-10-17-asset-phases.md, section 6): icons for the checklist and the
// minimap, the director's blackout, emotes over the tolis, motion cues and the props each game brings. Files live in
// public/sprites/hud/ (see its README for sources and licences). Nothing here draws smoke: smoke over a crowd reads as
// tear gas (section 10).
import type { GameMap, Vec } from '../shared/map';
import type { DrawEnt, Standing } from './render';
import { ChaloGeo, ChaloRules, ChaloSnap, CODE_GATE, GateState, LEVEL_SITE } from '../shared/chalo';
import { standing } from './props';
import { INK } from './ui';

// ------------------------------------------------------------------ files

/** Every HUD file the client draws, by the name code uses. Served from /sprites/hud/<file>. */
export const HUD = {
  // kenney-game-icons, white
  locked: 'locked.png', unlocked: 'unlocked.png', target: 'target.png', massiveMultiplayer: 'massiveMultiplayer.png',
  warning: 'warning.png', flag: 'flag.png', medal1: 'medal1.png', trophy: 'trophy.png', video: 'video.png',
  // kenney-minimap-pack style C, recoloured to a white glyph on clear so they tint by state
  'mm-closed': 'mm-closed.png', 'mm-game': 'mm-game.png', 'mm-open': 'mm-open.png', 'mm-closing': 'mm-closing.png',
  'mm-stage': 'mm-stage.png', 'mm-van': 'mm-van.png',
  // kenney-emotes-pack vector style 1
  'emote-dots': 'emote-dots.png', 'emote-sad': 'emote-sad.png', 'emote-happy': 'emote-happy.png',
  // kenney-particle-pack, downscaled
  'fx-dust': 'fx-dust.png', 'fx-ring': 'fx-ring.png', 'fx-flash': 'fx-flash.png',
  // drawn for Kafila (white; black twins sit beside them for light panels)
  'link-arms': 'link-arms-white.svg', sit: 'sit-white.svg', barricade: 'barricade-white.svg', megaphone: 'megaphone-white.svg',
  bus: 'bus-white.svg', metro: 'metro-white.svg', water: 'water-white.svg', dhol: 'dhol-white.svg', shield: 'shield-white.svg',
} as const;
export type HudName = keyof typeof HUD;

const imgs = new Map<HudName, HTMLImageElement>();
/** The loaded image, or null until it arrives (and always in tests). */
export function hudImg(name: HudName): HTMLImageElement | null {
  if (typeof Image === 'undefined') return null;
  let i = imgs.get(name);
  if (!i) { i = new Image(); i.src = `/sprites/hud/${HUD[name]}`; imgs.set(name, i); }
  return i.complete && i.naturalWidth ? i : null;
}

const tints = new Map<string, HTMLCanvasElement>();
/** A white icon recoloured, made once per name and colour. */
function tinted(name: HudName, colour: string): HTMLCanvasElement | HTMLImageElement | null {
  const im = hudImg(name);
  if (!im || colour === '#FFFFFF') return im;
  const key = name + colour;
  let c = tints.get(key);
  if (!c) {
    c = document.createElement('canvas'); c.width = im.naturalWidth; c.height = im.naturalHeight;
    const g = c.getContext('2d')!;
    g.drawImage(im, 0, 0); g.globalCompositeOperation = 'source-in'; g.fillStyle = colour; g.fillRect(0, 0, c.width, c.height);
    tints.set(key, c);
  }
  return c;
}

/** An icon in a size x size box with its top left at (x, y); the minimap's 8 px tiles stay crisp. */
export function drawIcon(g: CanvasRenderingContext2D, name: HudName, x: number, y: number, size: number, colour = '#FFFFFF', alpha = 1) {
  const im = tinted(name, colour);
  if (!im) return;
  const crisp = name.startsWith('mm-'), was = g.imageSmoothingEnabled, a = g.globalAlpha;
  g.imageSmoothingEnabled = !crisp; g.globalAlpha = a * alpha;
  const w = (im as HTMLImageElement).naturalWidth || im.width, h = (im as HTMLImageElement).naturalHeight || im.height, k = size / Math.max(w, h);
  g.drawImage(im, x + (size - w * k) / 2, y + (size - h * k) / 2, w * k, h * k);
  g.imageSmoothingEnabled = was; g.globalAlpha = a;
}

// ------------------------------------------------------------------ the director's clock

/** The blackout (director timeline event `blackout`, from atS for forS): what it hides and for how much longer. */
export function blackout(R: ChaloRules, t: number): { on: boolean; left: number; hides: string[] } {
  for (const ev of R.director.timeline) {
    if (ev.event !== 'blackout') continue;
    const end = ev.atS + (ev.forS ?? 0);
    if (t >= ev.atS && t < end) return { on: true, left: end - t, hides: (ev as { hides?: string[] }).hides ?? [] };
  }
  return { on: false, left: 0, hides: [] };
}

/** A metro gate is shut from its closesAtS (director event metroClosed) on. */
export const metroShut = (e: { closesAtS: number | null }, t: number) => e.closesAtS !== null && t >= e.closesAtS;

// ------------------------------------------------------------------ markers

/** A checkpoint's marker on the minimap: a minimap-pack glyph and a colour per state, so shape alone tells them apart. */
export interface Marker { state: GateState; tile: HudName; colour: string; label: string }
export const MARKERS: Record<GateState, Marker> = {
  closed: { state: 'closed', tile: 'mm-closed', colour: INK.riso, label: 'Shut' },
  game: { state: 'game', tile: 'mm-game', colour: INK.metro, label: 'Game on' },
  open: { state: 'open', tile: 'mm-open', colour: INK.newsprint, label: 'Open' },
  back: { state: 'back', tile: 'mm-closing', colour: INK.barricade, label: 'Closing' },
};
export const markerFor = (code: number): Marker => MARKERS[CODE_GATE[code] ?? 'closed'];

/** The ink a glyph takes on a badge of this colour: asphalt on the light inks, newsprint on the dark ones. */
const glyphOn = (colour: string) => (colour === INK.newsprint || colour === INK.metro || colour === INK.newsprint2 ? INK.asphalt : INK.newsprint);

/** A marker badge centred on (x, y): a tile in the state's colour, ink-edged, with a hard shadow and the glyph cut in. */
export function drawBadge(g: CanvasRenderingContext2D, name: HudName, colour: string, x: number, y: number, size: number, alpha = 1) {
  // the minimap's glyphs are 8 px pixel art: draw them at a whole multiple, on whole pixels, so they stay sharp
  const pix = name.startsWith('mm-'), inner = pix ? 8 * Math.max(1, Math.floor((size - 3) / 8)) : Math.round(size * 0.7);
  const s = inner + (pix ? 4 : Math.round(size * 0.3)), x0 = Math.round(x - s / 2), y0 = Math.round(y - s / 2), sh = size >= 16 ? 2 : 1;
  g.save(); g.globalAlpha = alpha;
  g.fillStyle = INK.asphalt; g.fillRect(x0 + sh, y0 + sh, s, s);
  g.fillStyle = colour; g.fillRect(x0, y0, s, s);
  g.strokeStyle = INK.asphalt; g.lineWidth = 1; g.strokeRect(x0 + 0.5, y0 + 0.5, s - 1, s - 1);
  drawIcon(g, name, x0 + (s - inner) / 2, y0 + (s - inner) / 2, inner, glyphOn(colour));
  g.restore();
}

/** Seconds left on an open gate at which the checklist warns of the re-close. */
export const WARN_S = 8;
/** The checklist's icon for a gate: a re-close coming, open, or locked (shut, a game on it, police walking back). */
export function gateIcon(code: number, left: number): HudName {
  const st = CODE_GATE[code];
  if (st === 'open') return left > 0 && left <= WARN_S ? 'warning' : 'unlocked';
  return 'locked';
}

/** Each game's icon in the checklist (Rasta has none yet). */
export const MISSION_ICON: Record<string, HudName | undefined> = {
  nazar: 'target', toli: 'massiveMultiplayer', baat: 'megaphone', rassakashi: 'dhol', jodi: 'link-arms', dharna: 'sit',
};

// ------------------------------------------------------------------ crowd emotes and motion cues

/** "…" over a toli after this long still in a queue: before waitBeforeLeavingS, so the crowd shows it is giving up first. */
export const QUEUE_MARK_S = 30;
const SAD_S = 3, CHEER_S = 2.5, FX_S = { dust: 0.9, ring: 1.4, flash: 0.35 };

export type EmoteKind = 'wait' | 'sad' | 'cheer';
export type FxKind = keyof typeof FX_S;
export interface Emote { id: number; x: number; y: number; kind: EmoteKind }
export interface Fx { kind: FxKind; x: number; y: number; at: number }
/** still: seconds stood still in all, as the sim counts a toli's wait; moving: it moved since the last snapshot */
interface ToliSeen { x: number; y: number; n: number; level: number; still: number; moving: boolean; mark: { kind: 'sad' | 'cheer'; until: number } | null }

/** Body ids from chalo.ts bodyId(toli, j): the j = 0 body stands on the toli's centre. */
const toliOfBody = (id: number) => (id >= 1_000_000 && (id - 1_000_000) % 8 === 0 ? (id - 1_000_000) / 8 : -1);

/**
 * Reads snapshot pairs (rows [id, 3, x, y, face, n] and the Chalo block) and keeps what the crowd shows: a toli still
 * in a queue for QUEUE_MARK_S, a toli that lost someone (its n dropped, or it vanished as the went-home count rose),
 * a toli through a gate (its ring level rose). Gate changes make motion cues: dust where a police line slides (at the
 * gap and where the squad stands aside), a ring at the van when a Nazar starts.
 */
export class CrowdCues {
  readonly tolis = new Map<number, ToliSeen>();
  private gone: { x: number; y: number; until: number }[] = [];
  fx: Fx[] = [];
  private t = -1;
  private cs: number[][] | null = null;
  private left = 0;
  constructor(readonly m: GameMap, readonly G: ChaloGeo, readonly R: ChaloRules) {}

  private levelAt(x: number, y: number) {
    const tx = Math.floor(x / this.m.tile), ty = Math.floor(y / this.m.tile);
    return tx >= 0 && ty >= 0 && tx < this.m.w && ty < this.m.h ? this.G.level[ty * this.m.w + tx] : 0;
  }

  /** One snapshot; repeats of the same clock are ignored. `wall` stamps motion cues for drawing (seconds). */
  update(rows: Map<number, number[]> | Iterable<number[]>, ch: ChaloSnap | null, wall = ch?.t ?? 0) {
    if (!ch || ch.t === this.t) return;
    if (ch.t < this.t) this.reset(); // a new round
    const dt = this.t < 0 ? 0 : ch.t - this.t;
    this.t = ch.t;
    const metre = this.m.tile / (this.m.mpt || 1), stillPx = this.R.crowd.speedMps * metre * dt * 0.2 + 1e-6;
    const seen = new Set<number>();
    for (const r of rows instanceof Map ? rows.values() : rows) {
      if (r[1] !== 3) continue;
      const id = r[0], x = r[2], y = r[3], n = r[5] ?? this.R.crowd.toliSize, level = this.levelAt(x, y);
      seen.add(id);
      const p = this.tolis.get(id);
      if (!p) { this.tolis.set(id, { x, y, n, level, still: 0, moving: false, mark: null }); continue; }
      // still time adds up as the sim's wait does (sim.ts tireToli): a shuffle in the queue doesn't start it over
      p.moving = Math.hypot(x - p.x, y - p.y) > stillPx;
      if (!p.moving) p.still += dt;
      if (n < p.n) p.mark = { kind: 'sad', until: ch.t + SAD_S };
      else if (level > p.level) { p.mark = { kind: 'cheer', until: ch.t + CHEER_S }; p.still = 0; }
      p.x = x; p.y = y; p.n = n; p.level = level;
    }
    const wentHome = (ch.c[3] ?? 0) > this.left;
    for (const [id, p] of this.tolis) if (!seen.has(id)) {
      if (wentHome) this.gone.push({ x: p.x, y: p.y, until: ch.t + SAD_S }); // its last person went home
      this.tolis.delete(id);
    }
    this.left = ch.c[3] ?? 0;
    if (this.cs) this.G.cps.forEach((cp, k) => {
      const a = CODE_GATE[this.cs![k]?.[0] ?? 0], b = CODE_GATE[ch.cs[k]?.[0] ?? 0];
      if (a === b) return;
      if (b === 'open' || b === 'back') { this.fx.push({ kind: 'dust', x: cp.at.x, y: cp.at.y, at: wall }, { kind: 'dust', x: cp.aside.x, y: cp.aside.y, at: wall }); }
      if (b === 'game' && cp.mission === 'nazar') this.fx.push({ kind: 'ring', x: this.G.van.x, y: this.G.van.y, at: wall });
    });
    this.cs = ch.cs.map((r) => r.slice());
    this.gone = this.gone.filter((g) => g.until > ch.t);
    this.fx = this.fx.filter((f) => wall - f.at < FX_S[f.kind]);
  }

  /** Hook for the press role (unbuilt): a flash where someone films. */
  press(x: number, y: number, wall: number) { this.fx.push({ kind: 'flash', x, y, at: wall }); }

  reset() { this.tolis.clear(); this.gone = []; this.fx = []; this.cs = null; this.t = -1; this.left = 0; }

  /** What shows now: a mark that hasn't run out, else "…" once a toli has queued QUEUE_MARK_S outside the site. */
  emotes(): Emote[] {
    const out: Emote[] = [];
    for (const [id, p] of this.tolis) {
      if (p.mark && p.mark.until > this.t) out.push({ id, x: p.x, y: p.y, kind: p.mark.kind });
      else if (p.still >= QUEUE_MARK_S && !p.moving && p.level < LEVEL_SITE) out.push({ id, x: p.x, y: p.y, kind: 'wait' });
    }
    for (const g of this.gone) out.push({ id: -1, x: g.x, y: g.y, kind: 'sad' });
    return out;
  }

  /**
   * Emotes over the tolis and the motion cues, in world coordinates (after the bodies). `ents` gives the drawn
   * positions; `heightOf` a body's height in world px. `wall` is the same clock as update's.
   */
  draw(g: CanvasRenderingContext2D, ents: DrawEnt[], z: number, wall: number, heightOf: (e: DrawEnt) => number) {
    const metre = this.m.tile / (this.m.mpt || 1);
    for (const f of this.fx) {
      const k = Math.min(1, (wall - f.at) / FX_S[f.kind]);
      const size = f.kind === 'dust' ? (2.5 + 3 * k) * metre : f.kind === 'ring' ? (4 + 22 * k) * metre : (5 - 2 * k) * metre;
      const name: HudName = f.kind === 'dust' ? 'fx-dust' : f.kind === 'ring' ? 'fx-ring' : 'fx-flash';
      drawIcon(g, name, f.x - size / 2, f.y - size / 2 - (f.kind === 'dust' ? metre * 0.5 : 0), size, f.kind === 'dust' ? '#D8CDB8' : f.kind === 'ring' ? INK.metro : '#FFFFFF', (1 - k) * (f.kind === 'dust' ? 0.75 : 1));
    }
    const at = new Map<number, DrawEnt>();
    for (const e of ents) { const t = toliOfBody(e.id); if (t >= 0) at.set(t, e); }
    const s = 24 / z, wall10 = wall * 10; // a bubble is 24 screen px tall at any zoom
    for (const em of this.emotes()) {
      const e = em.id >= 0 ? at.get(em.id) : undefined;
      const x = e ? e.x : em.x, y = (e ? e.y - heightOf(e) : em.y - metre * 3) - 4 / z;
      const bob = em.kind === 'cheer' ? Math.abs(Math.sin(wall10 * 0.6)) * 4 / z : 0;
      drawIcon(g, em.kind === 'wait' ? 'emote-dots' : em.kind === 'sad' ? 'emote-sad' : 'emote-happy', x - s / 2, y - s - bob, s);
    }
  }
}

// ------------------------------------------------------------------ per-game props

/**
 * The props each game brings (plan section 6), only while its checkpoint is in state `game` (code 1): Toli's
 * loudspeaker on the police side, Rassakashi's chained barricade across the gap and the dhol on the team's side,
 * Dharna's candles. For TreeLayer.draw(…, extra).
 */
export function gameProps(G: ChaloGeo, ch: ChaloSnap | null, metre: number): Standing[] {
  const out: (Standing | null)[] = [];
  if (!ch) return [];
  G.cps.forEach((cp, k) => {
    if (CODE_GATE[ch.cs[k]?.[0] ?? 0] !== 'game') return;
    // inward (police side) is towards the rings' centre; along the gap is across it
    const dx = G.centre.x - cp.at.x, dy = G.centre.y - cp.at.y, d = Math.hypot(dx, dy) || 1;
    const ix = dx / d, iy = dy / d, ax = -iy, ay = ix;
    const at = (inM: number, alongM: number): Vec => ({ x: cp.at.x + (ix * inM + ax * alongM) * metre, y: cp.at.y + (iy * inM + ay * alongM) * metre });
    const put = (key: string, p: Vec) => out.push(standing(key, p.x, p.y, metre));
    if (cp.mission === 'toli') put('oga-concert-props/speaker-001', at(4, 3));
    else if (cp.mission === 'rassakashi') { put('barricade_chain', at(0, 0)); put('dhol', at(-4, 2.5)); }
    else if (cp.mission === 'dharna') { put('candles', at(-2.5, -2)); put('candles', at(-2.5, 2)); }
  });
  return out.filter((s): s is Standing => !!s);
}

